// src/prototype/components/artifacts/ArtifactPreviewModal.tsx
// ============================================================
// Modal that previews a structured DvaArtifact using the
// ArtifactRenderer. Accepts artifact or builds it on open.
// Used by the Approval Artifacts section in EAActivatePage.
// ============================================================

import React from 'react';
import {
  X,
  Download,
  FileText,
  BarChart3,
  Shield,
  Layers,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';
import { DvaArtifact, ArtifactType, ARTIFACT_DISPLAY_LABELS } from '../../artifacts/types/artifactTypes';
import { ArtifactRenderer } from '../../artifacts/renderers/ArtifactRenderer';
import { exportArtifactPdf } from '../../exports/exportArtifactPdf';

const ARTIFACT_EXPORT_ROOT_ID = 'approval-artifact-export-root';

// ── Artifact type icon map ─────────────────────────────────────

const ARTIFACT_ICONS: Record<ArtifactType, React.ReactNode> = {
  executive_one_pager:     <FileText className="w-4 h-4" />,
  cfo_roi_summary:         <BarChart3 className="w-4 h-4" />,
  board_rationale:         <Shield className="w-4 h-4" />,
  assumptions_appendix:    <Layers className="w-4 h-4" />,
  implementation_packet:   <Layers className="w-4 h-4" />,
  strategic_execution_plan: <FileText className="w-4 h-4" />,
};

// ── Props ─────────────────────────────────────────────────────

interface ArtifactPreviewModalProps {
  artifact: DvaArtifact | null;
  loading?: boolean;
  error?: string | null;
  pendingType?: ArtifactType;
  onRetry?: () => void;
  onClose: () => void;
}

// ── Modal ─────────────────────────────────────────────────────

export const ArtifactPreviewModal: React.FC<ArtifactPreviewModalProps> = ({
  artifact,
  loading = false,
  error = null,
  pendingType,
  onRetry,
  onClose,
}) => {
  const [exportStatus, setExportStatus] = React.useState<'idle' | 'exporting' | 'done' | 'failed'>('idle');

  // Close on Escape
  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  React.useEffect(() => {
    if (exportStatus !== 'done' && exportStatus !== 'failed') return;
    const t = window.setTimeout(() => setExportStatus('idle'), 2000);
    return () => window.clearTimeout(t);
  }, [exportStatus]);

  if (!artifact && !loading && !error) return null;

  const displayType = artifact?.artifactType ?? pendingType ?? 'executive_one_pager';
  const typeLabel = ARTIFACT_DISPLAY_LABELS[displayType];
  const icon = ARTIFACT_ICONS[displayType];

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleExportPdf = async () => {
    if (!artifact || loading || error || exportStatus === 'exporting') return;
    setExportStatus('exporting');
    try {
      await exportArtifactPdf({
        elementId: ARTIFACT_EXPORT_ROOT_ID,
        artifact,
        clientIdentifier: artifact.clientId || 'DVA',
      });
      setExportStatus('done');
    } catch (err) {
      console.error('[ArtifactPreviewModal] PDF export failed:', err);
      setExportStatus('failed');
    }
  };

  const exportButtonLabel =
    exportStatus === 'exporting' ? 'Exporting…'
      : exportStatus === 'done' ? 'Downloaded'
        : exportStatus === 'failed' ? 'Failed'
          : 'Export';

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto py-6 px-4"
      onClick={handleBackdropClick}
    >
      {/* Modal panel */}
      <div className="w-full max-w-2xl bg-[#F5F7FA] rounded-2xl shadow-2xl border border-[#e5e7eb] overflow-hidden">

        {/* Modal header bar */}
        <div className="flex items-center justify-between gap-4 px-5 py-3.5 bg-white border-b border-[#e5e7eb]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-[#18365E]/10 flex items-center justify-center text-[#18365E] shrink-0">
              {icon}
            </div>
            <div className="min-w-0">
              <p className="text-[9px] font-bold text-[#717182] uppercase tracking-widest">{typeLabel}</p>
              <p className="text-sm font-semibold text-[#18365E] truncate">
                {artifact?.subtitle ?? (loading ? 'Loading…' : 'Preview unavailable')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={!artifact || loading || Boolean(error) || exportStatus === 'exporting'}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-[11px] font-semibold text-[#18365E] bg-white hover:bg-[#F5F7FA] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Export artifact as PDF"
            >
              <Download className={`w-3.5 h-3.5 ${exportStatus === 'exporting' ? 'animate-pulse' : ''}`} />
              {exportButtonLabel}
            </button>
            {/* Close */}
            <button
              onClick={onClose}
              className="p-1.5 text-[#717182] hover:text-[#18365E] hover:bg-[#F5F7FA] rounded-lg transition-colors"
              aria-label="Close artifact preview"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Artifact content */}
        <div className="p-5 overflow-y-auto max-h-[80vh]">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-[#18365E]/30 border-t-[#18365E] animate-spin" />
              <p className="text-sm text-[#717182]">Loading {typeLabel}…</p>
            </div>
          )}
          {!loading && error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-8 text-center">
              <AlertTriangle className="w-7 h-7 text-rose-500 mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#18365E] mb-1">Unable to load artifact</p>
              <p className="text-xs text-[#717182] mb-4">{error}</p>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-rose-200 rounded-lg text-xs font-semibold text-rose-900 hover:bg-rose-50"
                >
                  <RotateCcw className="w-3.5 h-3.5" />Retry
                </button>
              )}
            </div>
          )}
          {!loading && !error && artifact && (
            <div id={ARTIFACT_EXPORT_ROOT_ID} className="bg-[#F5F7FA]">
              <ArtifactRenderer artifact={artifact} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-white border-t border-[#e5e7eb] flex items-center justify-between">
          <p className="text-[10px] text-[#717182]">
            {artifact
              ? `v${artifact.metadata.version} · ${artifact.metadata.status} · Generated by ${artifact.metadata.generatedBy}`
              : loading
                ? 'Loading from API…'
                : '—'}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg hover:bg-[#F5F7FA] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
